
import { ThemeProvider } from "@/components/ui/theme-provider";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Dashboard from "./pages/Dashboard";
import Partners from "./pages/Partners";
import Leads from "./pages/Leads";
import LeadStatistics from "./pages/LeadStatistics";
import Reports from "./pages/Reports";
import Messages from "./pages/Messages";
import MessageStatistics from "./pages/MessageStatistics";
import LeadDetails from "./pages/LeadDetails";
import AdminLeadDetails from "./pages/AdminLeadDetails";
import PartnerLeadsDashboard from "./pages/PartnerLeadsDashboard";
import PartnersMap from "./pages/PartnersMap";
import Admins from "./pages/Admins";
import SiteSettings from "./pages/SiteSettings";
import AdminLayout from "./components/layouts/AdminLayout";
import AdminLogin from "./pages/AdminLogin";
import PartnerLogin from "./pages/PartnerLogin";
import PartnerDashboard from "./pages/PartnerDashboard";
import { LeadProvider } from "./context/LeadContext";
import { PartnerProvider } from "./context/PartnerContext";
import { AssignmentProvider } from "./context/AssignmentContext";
import EmailTrackingStats from "./pages/EmailTrackingStats";
import AssignmentStats from "./pages/AssignmentStats";
import PartnerReports from "./pages/PartnerReports";
import NewPartner from "./pages/NewPartner";
import EditPartner from "./pages/EditPartner";
import PartnerRegistration from "./pages/PartnerRegistration";
import ThankYou from "./pages/ThankYou";
import PartnerMiniPage from "./pages/PartnerMiniPage";

const queryClient = new QueryClient();

const App = () => (
  <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
    <QueryClientProvider client={queryClient}>
      <LeadProvider>
        <PartnerProvider>
          <AssignmentProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <Routes>
                  <Route path="/" element={<Index />} />
                  {/* Lead details page remains accessible to all */}
                  <Route path="/lead/:id" element={<LeadDetails />} />
                  {/* Thank you page */}
                  <Route path="/thank-you" element={<ThankYou />} />
                  <Route path="/mini/:leadId" element={<PartnerMiniPage />} />
                  {/* Partner routes */}
                  <Route path="/partner/login" element={<PartnerLogin />} />
                  <Route path="/partner/:partnerId/dashboard" element={<PartnerDashboard />} />
                  <Route path="/partner/register" element={<PartnerRegistration />} />
                  {/* Admin login page */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  {/* Protected admin routes - AdminLayout already has the context providers */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<Dashboard />} />
                    <Route path="partners" element={<Partners />} />
                    <Route path="partners/new" element={<NewPartner />} />
                    <Route path="partners/edit/:id" element={<EditPartner />} />
                    <Route path="partners/map" element={<PartnersMap />} />
                    <Route path="partners/:partnerId/dashboard" element={<PartnerLeadsDashboard />} />
                    <Route path="leads" element={<Leads />} />
                    <Route path="lead/:id" element={<AdminLeadDetails />} />
                    <Route path="leads/statistics" element={<LeadStatistics />} />
                    <Route path="leads/assignment" element={<AssignmentStats />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="reports/partners" element={<PartnerReports />} />
                    <Route path="messages" element={<Messages />} />
                    <Route path="messages/statistics" element={<MessageStatistics />} />
                    <Route path="email/statistics" element={<EmailTrackingStats />} />
                    <Route path="settings" element={<SiteSettings />} />
                    <Route path="admins" element={<Admins />} />
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </TooltipProvider>
          </AssignmentProvider>
        </PartnerProvider>
      </LeadProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
